import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import mixpanel from 'mixpanel-browser';
import { fetchProfile } from '../../../redux/slices/ProfileSlice';
import { fetchPaymentUser } from '../../../redux/slices/PaymentUserSlice';
import { RootState, AppDispatch } from '../../../redux/store';
import loginApi from '../../../apis/login';
import authenticationUtils from '../../../utils/authenticationUtils';
import { RequestStatus } from '../../../enums';
import routes from '../../../constants/routes';
import AdminOtpScreen from './AdminOtpScreen';
import { useAuthRedirect, saveNativeUserFlag } from '../utils/LoginAuthGuard';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

const AdminOtpScreenContainer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const email =
    (typeof window !== 'undefined' &&
      window.localStorage.getItem('admin_email')) ||
    '';

  const [otp, setOtp] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user, profileStatus } = useSelector(
    (state: RootState) => state.profile
  );
  const { paymentUser, paymentUserStatus } = useSelector(
    (state: RootState) => state.paymentUser
  );
  const [easyPayToken, setEasyPayToken] = useState<string | null>(null);
  const didNavigateRef = useRef(false);
  useAuthRedirect(email);

  useEffect(() => {
    const profileReady = user && profileStatus === RequestStatus.SUCCESS;
    const needsPaymentUser = !isEmpty(easyPayToken);
    const paymentUserReady =
      paymentUser && paymentUserStatus === RequestStatus.SUCCESS;

    if (!profileReady) return;
    if (needsPaymentUser && !paymentUserReady) return;
    if (didNavigateRef.current) return;

    mixpanel.people.set({
      userId: user?.id.toString(),
      $name: user?.name,
      $email: user?.email,
      role: user?.role,
      school: user?.school,
      $avatar: user?.profile
    });
    mixpanel.identify(user?.email);
    mixpanel.track('otp_success', {
      session_type: 'admin_app',
      'Otp Validated Successfully': new Date().toISOString(),
      user_email: user?.email
    });

    saveNativeUserFlag();
    setIsLoading(false);
    didNavigateRef.current = true;
    navigate(routes.ADMIN_HOME);
  }, [
    user,
    profileStatus,
    easyPayToken,
    paymentUser,
    paymentUserStatus,
    navigate
  ]);

  const handleAuthenticateOtp = async () => {
    setIsLoading(true);
    try {
      const response = await loginApi.authenticateOtp(email, parseInt(otp, 10));
      try {
        const easypayResponse = await loginApi.easypayLogin(email);
        const easypayToken = (easypayResponse as any)?.data?.token;

        if (easypayToken) {
          setEasyPayToken(easypayToken);
          authenticationUtils.saveEasypayToken(easypayToken);
          dispatch(fetchPaymentUser());
        }
      } catch (easypayError) {
        console.error('Failed to fetch easypay token:', easypayError);
      } finally {
        authenticationUtils.saveToken(
          (response as unknown as { token: string }).token
        );
        dispatch(fetchProfile());
      }
      setErrorMessage('');
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || (
          <>
            It seems the OTP you entered is incorrect.
            <br />
            Please try again with the correct OTP to log in.
          </>
        )
      );
      setIsLoading(false);
    }
  };

  return (
    <AdminOtpScreen
      otp={otp}
      onOtpChange={setOtp}
      onSubmit={handleAuthenticateOtp}
      errorMessage={errorMessage}
      email={email}
      isLoading={isLoading}
    />
  );
};

export default AdminOtpScreenContainer;
